import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, stageClass, priorityClass, fmtDate, CAD } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import {
  Building2, ArrowRight, AlertTriangle, Clock, FileText,
  GraduationCap, DollarSign, ClipboardCheck, Mail, CalendarClock,
} from "lucide-react";

const KPI = ({ label, value, icon: Icon, tone = "slate", testid }) => (
  <div data-testid={testid} className="bg-white rounded-xl border border-slate-200 p-4">
    <div className="flex items-start justify-between">
      <div>
        <div className="text-xs uppercase tracking-wide text-slate-500 font-medium">{label}</div>
        <div className="heading-font text-3xl font-bold mt-1 text-slate-900">{value ?? 0}</div>
      </div>
      <div className={`w-9 h-9 rounded-lg flex items-center justify-center bg-${tone}-50 text-${tone}-600`}>
        <Icon className="w-4 h-4" />
      </div>
    </div>
  </div>
);

const Section = ({ title, count, children }) => (
  <div className="space-y-2">
    <div className="flex items-center justify-between">
      <h2 className="heading-font text-lg font-semibold text-slate-900">{title}</h2>
      {typeof count === "number" && <span className="text-xs text-slate-500">{count} record{count === 1 ? "" : "s"}</span>}
    </div>
    <div className="space-y-2">{children}</div>
  </div>
);

const AppRow = ({ a, tone = "slate", extra, onClick }) => (
  <button
    data-testid={`mywork-app-${a.application_id}`}
    onClick={onClick}
    className={`w-full text-left bg-white p-3 rounded-lg border border-${tone}-200 hover:shadow-md transition-shadow flex items-center gap-3`}
  >
    <div className="flex-1 min-w-0">
      <div className="mono text-[11px] text-slate-500">{a.application_id}</div>
      <div className="heading-font font-semibold text-slate-900 truncate">{a.student_name}</div>
      <div className="text-xs text-slate-500 truncate">
        {a.college_code || a.college} · {a.program}
        {a.partner_name ? <> · <span className="text-slate-600">{a.partner_name}</span></> : null}
      </div>
    </div>
    {extra}
    <span className={`stage-badge ${stageClass(a.stage)}`}>{a.stage}</span>
    <ArrowRight className="w-4 h-4 text-slate-400" />
  </button>
);

const Empty = ({ text }) => (
  <div className="bg-white p-6 rounded-lg border border-slate-200 text-sm text-slate-500 text-center">{text}</div>
);

export default function MyWork() {
  const { user } = useAuth();
  const nav = useNavigate();
  const isManager = ["admin", "manager", "management"].includes(user?.role);
  const [scopeAs, setScopeAs] = useState(
    isManager ? "bdm" : (user?.role === "admissions_officer" ? "admissions_officer" : "bdm")
  );
  const [data, setData] = useState(null);

  useEffect(() => {
    const q = isManager ? `?scope_as=${scopeAs}` : "";
    api.get(`/b2b/my-work${q}`).then((r) => setData(r.data));
  }, [scopeAs, isManager]);

  const maxFunnel = useMemo(() => Math.max(1, ...((data?.funnel || []).map((f) => f.count))), [data]);

  if (!data) return <div className="text-slate-500">Loading…</div>;

  const role = data.role;
  const c = data.counts || {};

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="heading-font text-3xl font-bold text-slate-900">My Work</h1>
          <p className="text-sm text-slate-500 mt-1">
            Hi {user?.name?.split(" ")[0]} — {role === "bdm" ? "your partners and their applications." : role === "admissions_officer" ? "the applications assigned to you." : "your open items."}
          </p>
        </div>
        {isManager && (
          <div className="flex items-center gap-2">
            <span className="text-xs text-slate-500 uppercase tracking-wide">Preview as</span>
            <Select value={scopeAs} onValueChange={setScopeAs}>
              <SelectTrigger data-testid="mywork-scope" className="h-9 w-56 bg-white"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="bdm">BDM view</SelectItem>
                <SelectItem value="admissions_officer">Admissions Officer view</SelectItem>
              </SelectContent>
            </Select>
          </div>
        )}
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-6 gap-3">
        <KPI testid="kpi-total" label={role === "bdm" ? "Partner Apps" : "My Apps"} value={c.total} icon={FileText} tone="slate" />
        <KPI testid="kpi-docs-pending" label="Documents Pending" value={c.docs_pending} icon={ClipboardCheck} tone="amber" />
        <KPI testid="kpi-applied" label="Applied · Review" value={c.applied} icon={Mail} tone="sky" />
        <KPI testid="kpi-offers" label="Offers" value={c.offers} icon={GraduationCap} tone="emerald" />
        <KPI testid="kpi-overdue" label="Overdue Follow-ups" value={c.overdue_followups} icon={AlertTriangle} tone="rose" />
        <KPI testid="kpi-due-week" label="Due This Week" value={c.due_this_week} icon={CalendarClock} tone="orange" />
      </div>

      {/* Funnel */}
      {(data.funnel || []).length > 0 && (
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <h2 className="heading-font text-lg font-semibold text-slate-900 mb-3">Stage Funnel</h2>
          <div className="space-y-1.5">
            {data.funnel.map((f) => (
              <div key={f.stage} className="flex items-center gap-3">
                <div className="w-56 text-xs text-slate-600 shrink-0">{f.stage}</div>
                <div className="flex-1 h-6 bg-slate-100 rounded overflow-hidden">
                  <div className="h-full bg-orange-400/80" style={{ width: `${(f.count / maxFunnel) * 100}%` }} />
                </div>
                <div className="w-10 text-right mono text-xs text-slate-700">{f.count}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* BDM-specific: Partners portfolio + Finance snapshot */}
      {role === "bdm" && (
        <>
          {data.finance && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <KPI testid="kpi-comm-received" label="Commission Received" value={CAD(data.finance.commission_received || 0)} icon={DollarSign} tone="emerald" />
              <KPI testid="kpi-comm-outstanding" label="Outstanding Commissions" value={CAD(data.finance.outstanding_commissions || 0)} icon={DollarSign} tone="amber" />
              <KPI testid="kpi-partner-due" label="Payable to Partners" value={CAD(data.finance.partner_payments_due || 0)} icon={DollarSign} tone="rose" />
            </div>
          )}

          <Section title="My Partners" count={(data.partners || []).length}>
            {(data.partners || []).length === 0 ? (
              <Empty text="You have no assigned partners yet." />
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
                {data.partners.map((p) => (
                  <button
                    key={p.id}
                    data-testid={`mywork-partner-${p.partner_id}`}
                    onClick={() => nav(`/partners/${p.id}`)}
                    className="text-left bg-white p-4 rounded-lg border border-slate-200 hover:shadow-md transition-shadow"
                  >
                    <div className="flex items-start justify-between">
                      <div className="w-9 h-9 rounded-lg bg-slate-100 flex items-center justify-center">
                        <Building2 className="w-4 h-4 text-slate-600" />
                      </div>
                      <span className="mono text-[11px] text-slate-500">{p.partner_id}</span>
                    </div>
                    <div className="heading-font font-semibold text-slate-900 mt-2 truncate">{p.company_name}</div>
                    <div className="text-xs text-slate-500 truncate">{p.country}</div>
                    <div className="flex flex-wrap gap-1.5 mt-3">
                      <span className="stage-badge bg-slate-100 text-slate-700 border-slate-200">{p.total_apps} apps</span>
                      <span className="stage-badge bg-sky-50 text-sky-800 border-sky-200">{p.active_apps} active</span>
                      <span className="stage-badge bg-emerald-50 text-emerald-800 border-emerald-200">{p.admission_complete} enrolled</span>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </Section>

          <Section title="Recent Applications" count={(data.recent_apps || []).length}>
            {(data.recent_apps || []).length === 0 ? (
              <Empty text="No recent applications from your partners." />
            ) : (
              data.recent_apps.map((a) => (
                <AppRow
                  key={a.id}
                  a={a}
                  extra={<span className="text-xs text-slate-500 whitespace-nowrap">{fmtDate(a.date_received)}</span>}
                  onClick={() => nav(`/applications/${a.id}`)}
                />
              ))
            )}
          </Section>
        </>
      )}

      {/* Admissions Officer buckets */}
      {role === "admissions_officer" && (
        <>
          <Section title="Documents Pending" count={(data.action_docs_pending || []).length}>
            {(data.action_docs_pending || []).length === 0 ? <Empty text="No documents pending — nice work." /> : data.action_docs_pending.map((a) => (
              <AppRow key={a.id} a={a} tone="amber" onClick={() => nav(`/applications/${a.id}`)} />
            ))}
          </Section>
          <Section title="Applied · Under Review" count={(data.action_review || []).length}>
            {(data.action_review || []).length === 0 ? <Empty text="No files under review." /> : data.action_review.map((a) => (
              <AppRow key={a.id} a={a} tone="sky" onClick={() => nav(`/applications/${a.id}`)} />
            ))}
          </Section>
          <Section title="Offers to Send / Follow" count={(data.action_offers || []).length}>
            {(data.action_offers || []).length === 0 ? <Empty text="No offers pending action." /> : data.action_offers.map((a) => (
              <AppRow key={a.id} a={a} tone="emerald" onClick={() => nav(`/applications/${a.id}`)} />
            ))}
          </Section>
          <Section title="Fees Pending" count={(data.action_fees_pending || []).length}>
            {(data.action_fees_pending || []).length === 0 ? <Empty text="No fees pending." /> : data.action_fees_pending.map((a) => (
              <AppRow key={a.id} a={a} tone="orange" onClick={() => nav(`/applications/${a.id}`)} />
            ))}
          </Section>
        </>
      )}

      {/* Follow-up sections shared by both */}
      <Section title="Overdue Follow-ups" count={(data.overdue_followups || []).length}>
        {(data.overdue_followups || []).length === 0 ? <Empty text="Nothing overdue." /> : data.overdue_followups.map((a) => (
          <AppRow
            key={a.id}
            a={a}
            tone="rose"
            extra={<span className="stage-badge bg-rose-50 text-rose-800 border-rose-200">Due {fmtDate(a.next_followup_date)}</span>}
            onClick={() => nav(`/applications/${a.id}`)}
          />
        ))}
      </Section>

      <Section title="Due This Week" count={(data.due_this_week || []).length}>
        {(data.due_this_week || []).length === 0 ? <Empty text="Nothing scheduled in the next 7 days." /> : data.due_this_week.map((a) => (
          <AppRow
            key={a.id}
            a={a}
            tone="amber"
            extra={<span className="stage-badge bg-amber-50 text-amber-800 border-amber-200">{fmtDate(a.next_followup_date)}</span>}
            onClick={() => nav(`/applications/${a.id}`)}
          />
        ))}
      </Section>
    </div>
  );
}
